import { useEffect, useMemo, useRef, useState } from "react";
import anime from "animejs"; // v3.2.1
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Sparkles,
  PhoneCall,
  Leaf,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";

/**
 * ChatBoatWidget
 * - Floating button opens a premium chat panel
 * - Starts with curated suggestions (Q&A cards)
 * - Then provides a simple FAQ-driven chatbot
 * - CTA to connect with a real expert
 */
export default function ChatBoatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Hi! I’m your ProfileWise guide. Pick a question to get started 👇",
    },
  ]);
  const [input, setInput] = useState("");
  const panelRef = useRef(null);
  const listRef = useRef(null);

  // Knowledge base (super concise; tune as needed)
  const KB = useMemo(
    () => [
      {
        q: "What’s included in Profile Optimization?",
        a: "We refine your LinkedIn & Naukri: headline, summary, sections, and role-targeted keywords. Result: a recruiter-friendly, story-led profile.",
        tags: ["profile", "optimization", "linkedin"],
      },
      {
        q: "Resume Alignment + ATS Boost",
        a: "We re-align your resume with target roles and map keywords to ATS. Clean structure, quantified bullets, and human-readable language.",
        tags: ["resume", "ats", "keywords", "alignment"],
      },
      {
        q: "Industry-Ready Profiles for multiple roles",
        a: "We craft adjacent role variants (e.g., Data Analyst ↔ Business Analyst) with tailored headlines, highlights, and accomplishment bullets.",
        tags: ["industry", "roles", "multiple"],
      },
      {
        q: "How do you help me land interviews?",
        a: "Step-by-step: profile analysis → 1:1 guidance → mock interviews → real opportunities → iteration based on feedback.",
        tags: ["interview", "process", "opportunities"],
      },
      {
        q: "Pricing & offers?",
        a: "Limited-time launch pricing: Starter ₹499 (was ₹1,599), Professional 499 (was ₹2,599), Elite ₹2,999 (was ₹3,999). One-time fee, GST invoicing available.",
        tags: ["price", "pricing", "offer", "discount"],
      },
      {
        q: "Turnaround & support",
        a: "Typical turnaround: 48 hrs. Priority support on Professional & Elite. Lifetime optimization support on Elite.",
        tags: ["support", "turnaround", "48", "lifetime"],
      },
    ],
    []
  );

  const SUGGESTIONS = [
    "What’s included in Profile Optimization?",
    "Resume Alignment + ATS Boost",
    "Industry-Ready Profiles for multiple roles",
    "How do you help me land interviews?",
    "Pricing & offers?",
  ];

  // Open/close animation
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (open) {
      el.style.display = "block";
      anime({
        targets: el,
        translateY: [20, 0],
        opacity: [0, 1],
        duration: 280,
        easing: "easeOutQuad",
      });
    } else {
      anime({
        targets: el,
        translateY: [0, 10],
        opacity: [1, 0],
        duration: 200,
        easing: "easeOutQuad",
        complete: () => (el.style.display = "none"),
      });
    }
  }, [open]);

  // Auto-scroll to bottom
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, [messages, open]);

  // Animated header sparkles
  useEffect(() => {
    if (!open) return;
    const sparks = document.querySelectorAll(".cb-spark");
    anime({
      targets: sparks,
      translateY: [-2, 2],
      direction: "alternate",
      loop: true,
      duration: 2200,
      delay: anime.stagger(200),
      easing: "easeInOutSine",
    });
  }, [open]);

  const send = (text) => {
    if (!text.trim()) return;
    const userMsg = { role: "user", text };
    setMessages((m) => [...m, userMsg]);

    // find best KB match (very simple keyword match)
    const key = text.toLowerCase();
    const best =
      KB.find((item) => item.tags.some((t) => key.includes(t))) ||
      KB.find((item) => item.q.toLowerCase() === key) ||
      KB[0];

    // bot reply (simulated)
    setTimeout(() => {
      setMessages((m) => [
        ...m,
        { role: "bot", text: best.a },
        {
          role: "bot",
          text: "Want to talk to a human expert now? I can connect you for a quick 1:1.",
          cta: true,
        },
      ]);
    }, 300);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
    setInput("");
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 rounded-full p-4 shadow-xl text-white
                   bg-gradient-to-br from-indigo-600 to-purple-600 hover:shadow-2xl
                   transition will-change-transform hover:scale-105"
        aria-label="Open chat"
      >
        <MessageCircle size={22} />
      </button>

      {/* Panel */}
      <div
        ref={panelRef}
        style={{ display: "none" }}
        className="fixed bottom-24 right-6 z-50 w-[90vw] max-w-sm rounded-2xl overflow-hidden
                   bg-white border border-gray-200 shadow-2xl"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-white/20 grid place-items-center">
              <Bot size={18} />
            </div>
            <div className="flex-1">
              <div className="font-semibold leading-tight">
                ProfileWise ChatBoat
              </div>
              <div className="text-xs text-white/80">
                Instant answers • Human handoff
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-2 rounded-md hover:bg-white/10 transition"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Sparkles for subtle life */}
          <div className="absolute right-4 top-3 flex gap-2 opacity-70">
            <Sparkles className="cb-spark" size={14} />
            <Leaf className="cb-spark" size={14} />
            <Star className="cb-spark" size={14} />
          </div>
        </div>

        {/* Suggestions (only if user hasn’t typed yet beyond greeting) */}
        {messages.length <= 2 && (
          <div className="px-4 pt-4">
            <div className="text-xs font-semibold text-gray-500 mb-2">
              Quick questions
            </div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="text-[12px] px-3 py-1.5 rounded-full border border-indigo-200 text-indigo-700
                             hover:bg-indigo-50 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div
          ref={listRef}
          className="max-h-[50vh] overflow-y-auto px-4 py-3 space-y-3"
        >
          {messages.map((m, i) => (
            <div key={i} className="flex gap-2">
              {m.role === "bot" ? (
                <div className="mt-1 shrink-0 h-7 w-7 rounded-lg bg-indigo-100 text-indigo-700 grid place-items-center">
                  <Bot size={14} />
                </div>
              ) : (
                <div className="mt-1 shrink-0 h-7 w-7 rounded-lg bg-gray-100 text-gray-700 grid place-items-center">
                  <User size={14} />
                </div>
              )}

              <div className="flex-1">
                <div
                  className={[
                    "inline-block rounded-2xl px-3 py-2 text-sm leading-relaxed",
                    m.role === "bot"
                      ? "bg-gray-50 border border-gray-200"
                      : "bg-indigo-600 text-white",
                  ].join(" ")}
                >
                  {m.text}
                </div>

                {/* Human handoff CTA */}
                {m.cta && (
                  <div className="mt-2">
                    <Link
                      to="/contactUs"
                      className="inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1.5 rounded-full
                                 border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition"
                    >
                      <PhoneCall size={14} /> Connect with a real expert
                    </Link>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* WhatsApp CTA */}
        <div className="px-3 pb-2 pt-1 border-t bg-gray-50/50">
          <a
            href="https://wa.me/917057051648"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 19 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.255 3.713a8 8 0 0 0-5.684-2.36c-4.433 0-8.043 3.603-8.043 8.036 0 1.394.364 2.771 1.045 3.974l-1.164 4.26 4.354-1.14a8.06 8.06 0 0 0 3.8.957c4.434 0 8.044-3.61 8.044-8.043 0-2.145-.84-4.172-2.352-5.692zM4.283 13.11c-.76-.863-1.18-2.312-1.18-3.72a6.467 6.467 0 0 1 6.46-6.46 6.42 6.42 0 0 1 4.568 1.891 6.42 6.42 0 0 1 1.892 4.568 6.467 6.467 0 0 1-6.46 6.46c-1.258 0-2.596-.404-3.562-1.06l-2.343.609z"
                fill="#fff"
                style={{ fill: "#fff", fillOpacity: 1 }}
              ></path>
              <path
                d="M11.748 10.434c.182.064 1.148.539 1.346.641.198.103.333.15.38.23.048.08.048.475-.119.934s-.95.879-1.33.934c-.34.048-.768.072-1.242-.079a12 12 0 0 1-1.125-.412c-1.979-.854-3.27-2.842-3.364-2.976-.103-.143-.8-1.069-.8-2.035s.507-1.448.689-1.646a.72.72 0 0 1 .522-.246h.38c.12 0 .285-.047.444.34.166.396.562 1.362.61 1.465a.38.38 0 0 1 .015.349c-.063.134-.095.213-.198.324a8 8 0 0 1-.293.348c-.095.095-.198.206-.087.404.119.198.507.84 1.093 1.362.752.673 1.385.879 1.583.974s.309.079.428-.048c.118-.135.49-.578.625-.776s.261-.166.443-.095z"
                fill="#fff"
                style={{ fill: "#fff", fillOpacity: 1 }}
              ></path>
            </svg>
            Chat on WhatsApp
          </a>
        </div>

        {/* Input */}
        <form onSubmit={onSubmit} className="p-3 border-t bg-white">
          <div className="flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, pricing, ATS, timelines..."
              className="flex-1 text-sm px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg
                         bg-indigo-600 text-white hover:bg-indigo-700 transition"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
